import dayjs from '@dcp/shared/utils/dayjs'
import {
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@atrvd/saas-ui'
import type { BankingFilters, Facility } from '@/types/banking'
import { safeParseDate } from '@/lib/date-utils'

/**
 * Sentinel value for "All" option in select dropdowns
 * Using '__all__' to avoid collision with potential real facility IDs
 */
const ALL_VALUE = '__all__'

interface CrossCheckingFiltersProps {
  filters: BankingFilters
  onFiltersChange: (filters: BankingFilters) => void
  facilities: Facility[]
}

/**
 * Filter controls for cross-checking page
 * Provides date picker and facility dropdown
 */
export function CrossCheckingFilters({
  filters,
  onFiltersChange,
  facilities,
}: CrossCheckingFiltersProps) {
  // Derive date from filters prop - single source of truth
  // Use safeParseDate to handle invalid date strings gracefully
  const date = safeParseDate(filters.endDate) ?? new Date()

  const handleDateChange = (newDate: Date | undefined) => {
    onFiltersChange({
      ...filters,
      endDate: newDate ? dayjs(newDate).format('YYYY-MM-DD') : undefined,
    })
  }

  const handleFacilityChange = (facilityId: string) => {
    onFiltersChange({
      ...filters,
      facilityIds: facilityId === ALL_VALUE ? undefined : [facilityId],
    })
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      {/* Date Picker */}
      <DatePicker
        date={date}
        onDateChange={handleDateChange}
        placeholder="Pick a date"
      />

      {/* Facility Filter */}
      <Select
        value={filters.facilityIds?.[0] || ALL_VALUE}
        onValueChange={handleFacilityChange}
      >
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue placeholder="Facility" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Facilities</SelectItem>
          {facilities.map((facility) => (
            <SelectItem key={facility.id} value={facility.id}>
              {facility.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}